/* Home capture sections (concept B hero, owner-picked 2026-09-30; revised by the owner's feedback of 2026-09-30,
   docs/build/feedback-0929/home-r3.md). Loaded by sections/xp-home-devices on templates/index.json.
   1. Hero pager (phones): two buttons scroll the stage row; aria-pressed follows the visible stage.
      (Stage loops, poster → video crossfade and the pause toggles are xp-base.js / xp-motion.js: data-xp-inview,
      data-xfade, data-xp-pause; the Write card's app recording in S3 uses the same.)
   2. S3 "One app for everything you capture.", the Voice card's illustration: while the card is ≥ 40 % in view (tab
      visible, after `load`, not reduced motion, not Save-Data) the three utterances rise in one by one (Transcript),
      then the Overview tab shows the AI Summary; two rounds, then it rests on the Transcript end state (the markup's
      own state, which is also what reduced motion, Save-Data and no JS show). The call waveform moves only while it
      runs (.is-running). A focus-only pause toggle covers WCAG 2.2.2.
   3. S4 "Works with the tools you already use.": the three examples take turns (3.4 s each, two rounds, in view
      only); the active example's hairlines turn violet and a short dash runs out from XNote while the rotation runs.
      Hover, focus or a click on an example selects it and stops the rotation (the route stays lit, the dash stops).
      Reduced motion / Save-Data / no JS: the first example stays lit and nothing moves.
   Nothing here hides content. */
(() => {
  'use strict';
  const d = document, w = window, root = d.documentElement;
  const $ = (s, el = d) => el.querySelector(s);
  const $$ = (s, el = d) => Array.from(el.querySelectorAll(s));
  const rmq = w.matchMedia('(prefers-reduced-motion: reduce)');
  const conn = navigator.connection || {};
  const lite = () => root.classList.contains('xp-lite') || Boolean(conn.saveData) || /2g$/.test(conn.effectiveType || '');
  const still = () => rmq.matches || lite();
  const afterLoad = (fn) => (d.readyState === 'complete' ? fn() : w.addEventListener('load', fn, { once: true }));
  const hasIO = 'IntersectionObserver' in w;

  /* ---------- 1. Hero pager ---------- */
  function initPager() {
    const row = $('[data-cap-stages]');
    const pager = $('[data-cap-pager]');
    if (!row || !pager) return;
    const btns = $$('[data-cap-go]', pager);
    const cards = Array.from(row.children);
    const set = (i) => btns.forEach((b, j) => b.setAttribute('aria-pressed', String(i === j)));
    btns.forEach((b) => b.addEventListener('click', () => {
      const i = Number(b.dataset.capGo) || 0;
      const card = cards[i];
      if (!card) return;
      // The row is position: relative, so card.offsetLeft is in the row's scroll coordinates.
      const pad = parseFloat(getComputedStyle(row).paddingLeft) || 0;
      row.scrollTo({ left: card.offsetLeft - pad, behavior: rmq.matches ? 'auto' : 'smooth' });
      set(i);
    }));
    let raf = 0;
    row.addEventListener('scroll', () => {
      if (raf) return;
      raf = w.requestAnimationFrame(() => {
        raf = 0;
        const pad = parseFloat(getComputedStyle(row).paddingLeft) || 0;
        const mid = row.scrollLeft + row.clientWidth * 0.35;
        let best = 0;
        cards.forEach((c, i) => { if (c.offsetLeft - pad <= mid) best = i; });
        set(best);
      });
    }, { passive: true });
  }

  /* A small in-view step runner: runs onStep(i) → returns the wait (ms) before step i + 1, for `steps` steps, only while
     `el` is ≥ ratio in view, the tab is visible, the page has loaded and motion is allowed; then onRest(). The flag
     element gets .is-running while it runs. */
  function runner(el, ratio, steps, onStep, onRest, flagEl) {
    let visible = false, loaded = d.readyState === 'complete', timer = 0, step = 0, done = false, paused = false;
    const can = () => loaded && visible && !d.hidden && !still() && !done && !paused;
    const clear = () => { if (timer) { w.clearTimeout(timer); timer = 0; } };
    const flag = () => { if (flagEl) flagEl.classList.toggle('is-running', can()); };
    const next = () => {
      timer = 0;
      if (!can()) return;
      if (step >= steps) { done = true; flag(); onRest(); return; }
      const wait = onStep(step++);
      timer = w.setTimeout(next, wait || 1000);
    };
    const run = () => { flag(); if (can()) { if (!timer) next(); } else clear(); };
    if (hasIO) new IntersectionObserver((es) => es.forEach((e) => { visible = e.intersectionRatio >= ratio; run(); }), { threshold: [0, ratio] }).observe(el);
    d.addEventListener('visibilitychange', run);
    rmq.addEventListener('change', () => { if (still()) { clear(); onRest(); } run(); });
    afterLoad(() => { loaded = true; run(); });
    return {
      stop() { done = true; clear(); flag(); },
      pause(p) { paused = p; if (p) clear(); run(); },
    };
  }

  /* ---------- 2. S3: the Voice card's recording screen ---------- */
  function initOneApp() {
    $$('[data-oa-voice]').forEach((stage) => {
      const card = stage.closest('.xp-oa-card') || stage;
      const utts = $$('.xp-oa-utt', stage);
      const tabs = { overview: $('[data-oa-tab="overview"]', stage), transcript: $('[data-oa-tab="transcript"]', stage) };
      const panes = { overview: $('[data-oa-pane="overview"]', stage), transcript: $('[data-oa-pane="transcript"]', stage) };
      if (!utts.length || !tabs.overview || !tabs.transcript || !panes.overview || !panes.transcript) return;
      const show = (k) => {
        Object.keys(tabs).forEach((n) => { tabs[n].classList.toggle('is-on', n === k); panes[n].classList.toggle('is-on', n === k); });
        stage.setAttribute('data-oa-phase', k);
      };
      const rest = () => { show('transcript'); stage.removeAttribute('data-oa-phase'); utts.forEach((u) => u.classList.add('is-in')); };
      // One round = 6 steps: reset · utterance 1 · 2 · 3 · Overview (the summary) · hold.
      const plan = [
        () => { show('transcript'); utts.forEach((u) => u.classList.remove('is-in')); return 450; },
        () => { utts[0].classList.add('is-in'); return 1100; },
        () => { if (utts[1]) utts[1].classList.add('is-in'); return 1100; },
        () => { if (utts[2]) utts[2].classList.add('is-in'); return 1900; },
        () => { show('overview'); return 3600; },
        () => 300,
      ];
      const r = runner(stage, 0.4, plan.length * 2, (i) => plan[i % plan.length](), rest, card);
      // Focus-only pause toggle (WCAG 2.2.2), as on the other looping media.
      // The toggle sits in the stage beside the screen (the screen itself is role="img", so it can't hold a control).
      const btn = d.createElement('button');
      btn.type = 'button';
      btn.className = 'xp-media__pause';
      btn.setAttribute('aria-label', 'Pause the illustration');
      btn.setAttribute('aria-pressed', 'false');
      btn.innerHTML = '<svg class="xp-media__pause-i" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M8 5.5h3v13H8zM13 5.5h3v13h-3z" fill="currentColor"/></svg><svg class="xp-media__play-i" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M8 5.5v13l10.5-6.5z" fill="currentColor"/></svg>';
      btn.hidden = still();
      stage.appendChild(btn);
      let paused = false;
      btn.addEventListener('click', () => { paused = !paused; btn.setAttribute('aria-pressed', String(paused)); btn.setAttribute('aria-label', paused ? 'Play the illustration' : 'Pause the illustration'); r.pause(paused); if (paused) rest(); });
      rmq.addEventListener('change', () => { btn.hidden = still(); });
    });
  }

  /* ---------- 3. S4: the tools hub ---------- */
  const ROUTES = { tasks: ['todoist'], events: ['gcal', 'outlook'], export: ['gdrive', 'onedrive'] };
  function initTools() {
    $$('[data-tools]').forEach((sec) => {
      const exs = $$('[data-tools-ex]', sec);
      const hub = $('[data-tools-hub]', sec);
      if (!exs.length || !hub) return;
      // A pulse copy of every hairline (the dash that runs out from XNote along the active route).
      $$('.xp-tools-hub__wires path', hub).forEach((p) => {
        const c = p.cloneNode();
        c.classList.add('xp-tools-pulse');
        p.after(c);
      });
      const apps = $$('[data-app]', hub);
      const wires = $$('.xp-tools-hub__wires path', hub);
      const select = (key) => {
        const on = ROUTES[key] || [];
        exs.forEach((b) => { const m = b.dataset.toolsEx === key; b.classList.toggle('is-on', m); b.setAttribute('aria-pressed', String(m)); });
        apps.forEach((a) => a.classList.toggle('is-on', on.includes(a.dataset.app)));
        wires.forEach((p) => p.classList.toggle('is-on', on.includes(p.dataset.to)));
      };
      select(exs[0].dataset.toolsEx);
      const keys = exs.map((b) => b.dataset.toolsEx);
      const r = runner(sec, 0.35, keys.length * 2 + 1, (i) => { select(keys[i % keys.length]); return 3400; }, () => select(keys[0]), sec);
      const pick = (b) => { r.stop(); select(b.dataset.toolsEx); };
      exs.forEach((b) => {
        b.addEventListener('click', () => pick(b));
        b.addEventListener('focus', () => pick(b));
        b.addEventListener('mouseenter', () => { if (w.matchMedia('(hover: hover) and (pointer: fine)').matches) pick(b); });
      });
    });
  }

  /* Swipe rows (phones): a row that scrolls sideways gets a tab stop, so keyboard users can scroll it too. */
  function initRows() {
    const rows = $$('[data-oa-row]');
    if (!rows.length) return;
    const sync = () => rows.forEach((r) => { if (r.scrollWidth > r.clientWidth + 2) r.setAttribute('tabindex', '0'); else r.removeAttribute('tabindex'); });
    sync();
    w.addEventListener('resize', () => w.requestAnimationFrame(sync));
  }

  /* Feature visuals (Ask, Tasks, Share): each card plays once when ≥ 55 % of it is on screen. IntersectionObserver
     honours the swipe row's clipping, so on phones a card plays when it is swiped to, not when the row appears. */
  function initFeats() {
    const cards = $$('[data-oa-feat]');
    if (!cards.length) return;
    if (!('IntersectionObserver' in w)) { cards.forEach((c) => c.classList.add('is-on')); return; }
    const io = new IntersectionObserver((entries) => entries.forEach((e) => {
      if (e.isIntersecting) { e.target.classList.add('is-on'); io.unobserve(e.target); }
    }), { threshold: 0.55 });
    cards.forEach((c) => io.observe(c));
  }

  const init = () => { initPager(); initOneApp(); initTools(); initRows(); initFeats(); };
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', init, { once: true }); else init();
})();
